import { extractDocument } from "scanic";
import { createTargetFrame } from "./DocumentGeometry";

const PREVIEW_IMAGE_TYPE = "image/jpeg";
const PREVIEW_IMAGE_QUALITY = 0.94;

const isValidNormalizedPoint = (point) =>
  Number.isFinite(point?.x) &&
  Number.isFinite(point?.y) &&
  point.x >= 0 &&
  point.x <= 1 &&
  point.y >= 0 &&
  point.y <= 1;

export const normalizedCornersToPixelMap = (
  corners,
  frameWidth,
  frameHeight,
) => {
  if (
    corners?.length !== 4 ||
    !corners.every(isValidNormalizedPoint) ||
    !frameWidth ||
    !frameHeight
  ) {
    return null;
  }

  const [topLeft, topRight, bottomRight, bottomLeft] = corners.map(
    (corner) => ({
      x: corner.x * frameWidth,
      y: corner.y * frameHeight,
    }),
  );

  return { topLeft, topRight, bottomRight, bottomLeft };
};

const canvasToBlob = (canvas) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("The cropped document preview is unavailable."));
      },
      PREVIEW_IMAGE_TYPE,
      PREVIEW_IMAGE_QUALITY,
    );
  });

/**
 * 把追踪器的归一化四角还原为高清截图像素坐标，再由 Scanic 做透视校正。
 * 这里只生成用户看到的预览图；失败时 DocumentCamera 会回退到未裁切截图。
 */
export const cropDocumentPreview = async (sourceCanvas, normalizedCorners) => {
  const pixelCorners = normalizedCornersToPixelMap(
    normalizedCorners,
    sourceCanvas?.width,
    sourceCanvas?.height,
  );

  if (!sourceCanvas?.width || !sourceCanvas?.height || !pixelCorners) {
    throw new Error("Four valid document corners are required for cropping.");
  }

  const result = await extractDocument(sourceCanvas, pixelCorners, {
    output: "canvas",
  });

  if (!result?.success || !result.output?.width || !result.output?.height) {
    throw new Error(result?.message || "Document perspective crop failed.");
  }

  return {
    blob: await canvasToBlob(result.output),
    width: result.output.width,
    height: result.output.height,
    pixelCorners,
  };
};

// 手动模式不做实时证件检测，直接按屏幕取景框对应的源视频区域裁切。
export const cropDocumentPreviewToGuide = (sourceCanvas) => {
  if (!sourceCanvas?.width || !sourceCanvas?.height) {
    throw new Error("A readable camera frame is required for guide cropping.");
  }

  const frameAspectRatio = sourceCanvas.width / sourceCanvas.height;
  return cropDocumentPreview(
    sourceCanvas,
    createTargetFrame(frameAspectRatio).corners,
  );
};
